import { FC, useMemo, useState } from 'react'
import moment from 'moment'
import { EChartOption } from 'echarts'
import { EChart } from 'src/charts/EChart'
import { TrendBadge } from 'src/components/TrendBadge'
import { useAdPerfTrends } from 'src/reactQuery/useAdPerformance'
import { AdPerfRange } from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import { PillSelect } from './PillSelect'
import { compact, money, pct } from '../format'

const METRICS = [
  { group: 'Cost', key: 'spend', label: 'Total Spend' },
  { group: 'Cost', key: 'cpc', label: 'Avg. CPC' },
  { group: 'Cost', key: 'cpm', label: 'CPM' },
  { group: 'Reach', key: 'clicks', label: 'Total Clicks' },
  { group: 'Reach', key: 'impressions', label: 'Impressions' },
  { group: 'Reach', key: 'ctr', label: 'CTR' },
  { group: 'Results', key: 'link_clicks', label: 'Link clicks' },
  { group: 'Results', key: 'leads', label: 'Leads' },
  { group: 'Results', key: 'engagements', label: 'Engagement' },
] as const

const MONEY = ['spend', 'cpc', 'cpm']
const PERCENT = ['ctr']

interface Props {
  currency: string
  range: AdPerfRange
}

export const PerformanceTrends: FC<Props> = ({ currency, range }) => {
  const [metric, setMetric] = useState<string>('spend')
  const { data } = useAdPerfTrends(range, metric)
  const fmt = (v: number | null) =>
    MONEY.includes(metric)
      ? money(v, currency, metric === 'spend' ? undefined : 2)
      : PERCENT.includes(metric)
        ? pct(v)
        : compact(v)

  const axisFmt = (v: number) =>
    MONEY.includes(metric)
      ? money(v, currency, metric === 'spend' ? 0 : 2)
      : PERCENT.includes(metric)
        ? `${v}%`
        : compact(v)

  const options = useMemo<EChartOption>(
    () => ({
      grid: { bottom: 28, containLabel: true, left: 8, right: 16, top: 12 },
      series: [
        {
          areaStyle: {
            color: {
              colorStops: [
                { color: 'rgba(128,128,255,0.34)', offset: 0 },
                { color: 'rgba(128,128,255,0.03)', offset: 1 },
              ],
              global: false,
              type: 'linear',
              x: 0,
              x2: 0,
              y: 0,
              y2: 1,
            },
          },
          data: data?.series.map((p) => p.value ?? '-') ?? [],
          itemStyle: { borderColor: '#fff', borderWidth: 2, color: '#8080ff' },
          lineStyle: { color: '#8080ff', width: 2 },
          name: 'Meta',
          showSymbol: false,
          smooth: true,
          symbolSize: 8,
          type: 'line',
        },
      ],
      tooltip: {
        backgroundColor: '#fff',
        borderColor: '#ececf6',
        extraCssText:
          'border-radius:12px;box-shadow:0 8px 24px rgba(37,38,45,.08);',
        formatter: (ps: unknown) => {
          const p = (ps as { dataIndex: number; value: number | string }[])[0]
          const d = data?.series[p.dataIndex]?.date
          return `<div style="font:12px Roboto;color:rgba(37,38,45,.6)">${moment(
            d
          ).format(
            'MMM D, YYYY'
          )}</div><div style="font:500 14px Roboto;color:#25262d;margin-top:4px">Meta: ${fmt(
            typeof p.value === 'number' ? p.value : null
          )}</div>`
        },
        padding: [10, 14],
        trigger: 'axis',
      },
      xAxis: {
        axisLabel: { color: 'rgba(37,38,45,0.6)', fontSize: 12, margin: 12 },
        axisLine: { lineStyle: { color: '#ececf6' } },
        axisTick: { show: false },
        // Inset the first/last date like the design; flush to the y-axis the
        // first date sat on top of the "$0.00" label.
        boundaryGap: true,
        data: data?.series.map((p) => moment(p.date).format('MMM D')) ?? [],
        type: 'category',
      },
      yAxis: {
        axisLabel: {
          color: 'rgba(37,38,45,0.6)',
          fontSize: 12,
          formatter: (v: number) => axisFmt(v),
          margin: 12,
        },
        splitLine: { lineStyle: { color: '#ececf6', type: 'dashed' } },
        type: 'value',
      },
    }),
    [data, metric, currency]
  )

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <h3 className={s.cardTitle}>Performance Trends</h3>
        <PillSelect
          label="Metric"
          onChange={setMetric}
          options={METRICS.map((m) => ({
            group: m.group,
            label: m.label,
            value: m.key,
          }))}
          searchable
          value={metric}
        />
      </div>
      <div className={s.bigNumber}>
        {fmt(data?.total ?? null)}
        <TrendBadge
          lowerIsBetter={['cpc', 'cpm'].includes(metric)}
          value={data?.change}
        />
      </div>
      {/* The app layout has zoom: 0.9, which ECharts doesn't account for:
          the hover line lands off the cursor. Undo the zoom around the chart.
          Remount on new data: the EChart wrapper sets options once. */}
      <div className={s.unzoom}>
        <EChart
          height={270}
          key={JSON.stringify(data?.series)}
          options={options}
        />
      </div>
      <div className={s.legend} style={{ justifyContent: 'center' }}>
        <span>
          <i className={s.legendLine} style={{ color: '#8080ff' }} />
          Meta
        </span>
      </div>
    </div>
  )
}
